import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { CCTVCamera, DomeCCTV, PTZCamera, DVRUnit, ShieldIcon } from '../components/Illustrations';
import './Services.css';

const Services = () => {
  const { t } = useTranslation();
  const refs = useRef([]);

  const services = [
    {
      id: 'installation',
      icon: <CCTVCamera size={100} />,
      badge: t('services.installation.badge'),
      title: t('services.installation.title'),
      subtitle: t('services.installation.subtitle'),
      desc: t('services.installation.desc'),
      features: t('services.installation.features'),
      brands: ['Hikvision', 'Dahua', 'CP Plus', 'Bosch'],
      color: 'var(--navy)',
    },
    {
      id: 'dvr',
      icon: <DVRUnit size={140} />,
      title: t('services.dvr.title'),
      subtitle: t('services.dvr.subtitle'),
      desc: t('services.dvr.desc'),
      features: t('services.dvr.features'),
      brands: ['Hikvision', 'Dahua', 'CP Plus'],
      color: 'var(--navy)',
    },
    {
      id: 'amc',
      icon: <ShieldIcon size={90} color="var(--green)" />,
      title: t('services.amc.title'),
      subtitle: t('services.amc.subtitle'),
      desc: t('services.amc.desc'),
      features: t('services.amc.features'),
      brands: [],
      highlight: t('services.amc.highlight'),
      color: 'var(--green)',
    },
    {
      id: 'remote',
      icon: <PTZCamera size={100} />,
      title: t('services.remote.title'),
      subtitle: t('services.remote.subtitle'),
      desc: t('services.remote.desc'),
      features: t('services.remote.features'),
      brands: [],
      color: 'var(--navy)',
    },
    {
      id: 'repair',
      icon: <DomeCCTV size={100} />,
      title: t('services.repair.title'),
      subtitle: t('services.repair.subtitle'),
      desc: t('services.repair.desc'),
      features: t('services.repair.features'),
      brands: [],
      color: 'var(--navy)',
    },
    {
      id: 'gem',
      icon: <ShieldIcon size={90} color="var(--saffron)" />,
      title: t('services.gem.title'),
      subtitle: t('services.gem.subtitle'),
      desc: t('services.gem.desc'),
      features: t('services.gem.features'),
      brands: [],
      highlight: t('services.gem.highlight'),
      link: '/gem',
      color: 'var(--saffron)',
    },
  ];

  const brands = [
    { name: t('services.brands.hikvision.name'), desc: t('services.brands.hikvision.desc') },
    { name: t('services.brands.dahua.name'), desc: t('services.brands.dahua.desc') },
    { name: t('services.brands.cpplus.name'), desc: t('services.brands.cpplus.desc') },
    { name: t('services.brands.bosch.name'), desc: t('services.brands.bosch.desc') },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.06 }
    );
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  return (
    <div>
      <section className="page-hero">
        <div className="container page-hero__inner">
          <div className="reveal" ref={add}>
            <div className="section-badge">{t('services.hero.badge')}</div>
            <h1>{t('services.hero.title1')}<br /><em style={{ fontStyle: 'italic', color: 'var(--green)' }}>{t('services.hero.title2')}</em></h1>
            <p style={{ color: 'var(--text-mid)', fontSize: '1.05rem', lineHeight: '1.75', maxWidth: '520px', marginTop: '1rem' }}>
              {t('services.hero.subtitle')}
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
              <Link to="/order" className="btn btn-green">{t('services.hero.getFreeSurvey')}</Link>
              <Link to="/gem" className="btn btn-saffron">{t('services.hero.gemPortal')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES DETAIL */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="services-list">
            {services.map((s, i) => (
              <div key={s.id} className={`service-detail ${i % 2 === 1 ? 'service-detail--reverse' : ''} reveal`} ref={add}>
                <div className="service-detail__visual">
                  <div className="service-detail__icon-wrap" style={{ borderColor: s.color }}>
                    {s.icon}
                  </div>
                  {s.badge && <div className="service-badge">{s.badge}</div>}
                  {s.highlight && <div className="service-highlight" style={{ borderColor: s.color === 'var(--saffron)' ? 'rgba(249,115,22,0.3)' : 'rgba(30,138,76,0.3)', background: s.color === 'var(--saffron)' ? 'var(--saffron-light)' : 'var(--green-light)', color: s.color }}>{s.highlight}</div>}
                  {s.brands.length > 0 && (
                    <div className="service-brands">
                      <p>Brands we install:</p>
                      <div className="service-brands__list">
                        {s.brands.map(b => <span key={b}>{b}</span>)}
                      </div>
                    </div>
                  )}
                  {s.link && <Link to={s.link} className="btn btn-saffron" style={{ marginTop: '1rem', display: 'inline-flex' }}>View GEM Portal →</Link>}
                </div>
                <div className="service-detail__content">
                  <div className="section-badge" style={s.color === 'var(--green)' ? { background: 'var(--green-light)', color: 'var(--green)', borderColor: 'rgba(30,138,76,0.2)' } : s.color === 'var(--saffron)' ? { background: 'var(--saffron-light)', color: 'var(--saffron)', borderColor: 'rgba(249,115,22,0.2)' } : {}}>
                    {s.subtitle}
                  </div>
                  <h2 className="service-detail__title">{s.title}</h2>
                  <p className="service-detail__desc">{s.desc}</p>
                  <div className="service-features">
                    {s.features.map(f => (
                      <div key={f} className="service-feature">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        {f}
                      </div>
                    ))}
                  </div>
                  <Link to="/order" className="btn btn-primary" style={{ marginTop: '1.5rem' }}>Enquire Now →</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="section-padding" style={{ background: 'var(--blue-pale)', borderTop: '1px solid var(--border)' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge">{t('services.brands.badge')}</div>
            <h2>{t('services.brands.title')}</h2>
            <p style={{ color: 'var(--text-mid)', marginTop: '0.5rem' }}>{t('services.brands.subtitle')}</p>
          </div>
          <div className="brands-grid">
            {brands.map((b, i) => (
              <div key={b.name} className="brand-card reveal" ref={add} style={{ transitionDelay: `${i * 0.1}s` }}>
                <h3 className="brand-name">{b.name}</h3>
                <p className="brand-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: 'var(--navy)', padding: '5rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'white', fontFamily: 'Fraunces', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', marginBottom: '0.8rem' }}>{t('services.cta.title')}</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>{t('services.cta.subtitle')}</p>
          <Link to="/order" className="btn btn-green">{t('services.cta.button')}</Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
